import { writeSourcesPickerPack } from './sourcesPickerStorage.js';
import { normalizeAttributionTags, normalizeSourceKeywords } from './BlockAttribution.js';

export const AGENCY_MEDIA_MIME = 'application/x-naxos-agency-media';
export const AGENCY_MEDIA_TEXT_PREFIX = 'naxos-source-media:';
export const SOURCE_USAGE_STORAGE_KEY = 'naxos_pending_source_usages';
export const SOURCE_MEDIA_PACK_STORAGE_KEY = 'naxos_pending_agency_media_pack';
export const SOURCE_MEDIA_DRAGGING_KEY = 'naxos_source_media_dragging';

/**
 * @param {unknown} content
 * @returns {Array<{type: string, data: object}>}
 */
export function extractAgencyMediaBlocks(content) {
	const blocks = content?.blocks;
	if (!Array.isArray(blocks)) {
		return [];
	}

	return blocks.filter((block) => block && (block.type === 'image' || block.type === 'gallery'));
}

/**
 * @param {Array<{type?: string, data?: object}>|null|undefined} blocks
 * @returns {string[]}
 */
export function flattenAgencyMediaUrls(blocks) {
	const urls = [];

	if (!Array.isArray(blocks)) {
		return urls;
	}

	for (const block of blocks) {
		if (block?.type === 'image') {
			const url = block.data?.file?.url;
			if (typeof url === 'string' && url !== '') {
				urls.push(url);
			}
			continue;
		}

		if (block?.type === 'gallery') {
			for (const file of block.data?.files ?? []) {
				const url = typeof file === 'string' ? file : file?.url;
				if (typeof url === 'string' && url !== '') {
					urls.push(url);
				}
			}
		}
	}

	return urls;
}

/**
 * Strip CMS navigation query flags accidentally injected into media hrefs.
 *
 * @param {string} url
 * @returns {string}
 */
export function cleanAgencyMediaUrl(url) {
	if (typeof url !== 'string' || url === '') {
		return url;
	}

	try {
		const base = typeof window !== 'undefined' && window.location?.origin ? window.location.origin : 'https://example.test';
		const parsed = new URL(url, base);
		parsed.searchParams.delete('popup');
		parsed.searchParams.delete('offcanvas');
		return parsed.toString();
	} catch {
		return url.replace(/([?&])(popup|offcanvas)=true&?/g, '$1').replace(/[?&]$/, '');
	}
}

/**
 * Persist a source usage in sessionStorage (shared across same-origin iframes/panels).
 *
 * @param {string|null|undefined} source
 * @param {string|number|null|undefined} identifier
 */
export function persistSourceUsage(source, identifier) {
	if (!source || identifier == null || identifier === '') {
		return;
	}

	const entry = {
		source: String(source).trim(),
		identifier: String(identifier).trim(),
	};
	const key = `${entry.source}::${entry.identifier}`;

	let list = [];
	try {
		const raw = sessionStorage.getItem(SOURCE_USAGE_STORAGE_KEY);
		list = raw ? JSON.parse(raw) : [];
		if (!Array.isArray(list)) {
			list = [];
		}
	} catch {
		list = [];
	}

	if (!list.some((item) => `${item.source}::${item.identifier}` === key)) {
		list.push(entry);
		try {
			sessionStorage.setItem(SOURCE_USAGE_STORAGE_KEY, JSON.stringify(list));
		} catch {
			// Quota / private mode: ignore, in-memory queue still runs.
		}
	}
}

/**
 * Read and clear usages persisted for the next content save.
 *
 * @returns {Array<{source: string, identifier: string}>}
 */
export function consumePersistedSourceUsages() {
	let list = [];
	try {
		const raw = sessionStorage.getItem(SOURCE_USAGE_STORAGE_KEY);
		list = raw ? JSON.parse(raw) : [];
		if (!Array.isArray(list)) {
			list = [];
		}
		sessionStorage.removeItem(SOURCE_USAGE_STORAGE_KEY);
	} catch {
		list = [];
	}

	return list.filter((item) => item?.source && item?.identifier);
}

/**
 * Persist the current drag pack for same-origin iframe drops that lose DataTransfer MIME types.
 *
 * @param {{source: string|null, identifier: string|null, agency_id?: string|null, blocks: Array<{type: string, data: object}>}} pack
 */
export function persistSourceMediaPack(pack) {
	if (!pack?.blocks?.length) {
		return;
	}

	try {
		sessionStorage.setItem(
			SOURCE_MEDIA_PACK_STORAGE_KEY,
			JSON.stringify({
				source: pack.source ?? null,
				identifier: pack.identifier ?? pack.agency_id ?? null,
				agency_id: pack.identifier ?? pack.agency_id ?? null,
				signature: pack.signature ?? null,
				keywords: normalizeSourceKeywords(pack.keywords) ?? null,
				tag_ids: normalizeAttributionTags(pack.tag_ids),
				blocks: pack.blocks,
				ts: Date.now(),
			}),
		);
	} catch {
		// Quota / private mode: ignore.
	}
}

/**
 * Read the latest drag pack if still fresh (60s). Does not clear — drop may retry.
 *
 * @returns {{source: string|null, identifier: string|null, agency_id: string|null, blocks: Array<{type: string, data: object}>}|null}
 */
export function readPersistedSourceMediaPack() {
	try {
		const raw = sessionStorage.getItem(SOURCE_MEDIA_PACK_STORAGE_KEY);
		if (!raw) {
			return null;
		}

		const parsed = JSON.parse(raw);
		if (!parsed || !Array.isArray(parsed.blocks) || parsed.blocks.length === 0) {
			return null;
		}

		if (typeof parsed.ts === 'number' && Date.now() - parsed.ts > 60_000) {
			sessionStorage.removeItem(SOURCE_MEDIA_PACK_STORAGE_KEY);
			return null;
		}

		const identifier =
			parsed.identifier != null
				? String(parsed.identifier)
				: parsed.agency_id != null
					? String(parsed.agency_id)
					: null;

		return {
			source: parsed.source != null && parsed.source !== '' ? String(parsed.source) : null,
			identifier,
			agency_id: identifier,
			signature:
				parsed.signature != null && String(parsed.signature).trim() !== ''
					? String(parsed.signature).trim()
					: null,
			keywords: normalizeSourceKeywords(parsed.keywords),
			tag_ids: normalizeAttributionTags(parsed.tag_ids),
			blocks: parsed.blocks,
			ts: typeof parsed.ts === 'number' ? parsed.ts : null,
		};
	} catch {
		return null;
	}
}

/**
 * Resolve usage to stamp on a media block after native URL upload/drop.
 *
 * @param {string|null|undefined} mediaUrl
 * @returns {{source: string, identifier: string}|null}
 */
export function resolvePendingSourceUsageForMedia(mediaUrl = null) {
	const pending = typeof window !== 'undefined' ? window.__pendingSourceUsage : null;
	if (pending?.source && pending?.identifier) {
		return {
			source: String(pending.source),
			identifier: String(pending.identifier),
		};
	}

	const pack = readPersistedSourceMediaPack();
	if (pack?.source && pack?.identifier) {
		const cleaned_media = mediaUrl ? cleanAgencyMediaUrl(String(mediaUrl)) : '';
		const pack_urls = flattenAgencyMediaUrls(pack.blocks).map((url) => cleanAgencyMediaUrl(url));
		const url_matches =
			cleaned_media !== '' &&
			pack_urls.some(
				(url) =>
					url &&
					(cleaned_media === url ||
						cleaned_media.includes(url) ||
						url.includes(cleaned_media) ||
						String(mediaUrl).includes(url) ||
						url.includes(String(mediaUrl))),
			);

		const is_fresh = typeof pack.ts === 'number' && Date.now() - pack.ts < 15_000;
		if (url_matches || isSourceMediaDragging() || is_fresh) {
			return {
				source: String(pack.source),
				identifier: String(pack.identifier),
			};
		}
	}

	try {
		const raw = sessionStorage.getItem(SOURCE_USAGE_STORAGE_KEY);
		const list = raw ? JSON.parse(raw) : [];
		if (!Array.isArray(list) || list.length === 0) {
			return null;
		}

		if (isSourceMediaDragging() || list.length === 1) {
			const last = list[list.length - 1];
			if (last?.source && last?.identifier) {
				return {
					source: String(last.source),
					identifier: String(last.identifier),
				};
			}
		}
	} catch {
		// Ignore.
	}

	return null;
}

/**
 * Clear the persisted drag pack after a successful insert.
 */
export function clearPersistedSourceMediaPack() {
	try {
		sessionStorage.removeItem(SOURCE_MEDIA_PACK_STORAGE_KEY);
	} catch {
		// Ignore.
	}
}

/**
 * @param {boolean} is_dragging
 */
export function setSourceMediaDragging(is_dragging) {
	try {
		if (is_dragging) {
			sessionStorage.setItem(SOURCE_MEDIA_DRAGGING_KEY, '1');
			return;
		}

		sessionStorage.removeItem(SOURCE_MEDIA_DRAGGING_KEY);
	} catch {
		// Ignore.
	}
}

/**
 * @returns {boolean}
 */
export function isSourceMediaDragging() {
	try {
		return sessionStorage.getItem(SOURCE_MEDIA_DRAGGING_KEY) === '1';
	} catch {
		return false;
	}
}

/**
 * Queue a source usage for the next content save (no remote call here).
 *
 * @param {string|null|undefined} source
 * @param {string|number|null|undefined} identifier
 */
export function queueSourceUsage(source, identifier) {
	if (!source || identifier == null || identifier === '') {
		return;
	}

	persistSourceUsage(source, identifier);

	document.dispatchEvent(
		new CustomEvent('naxos:source-usage', {
			detail: {
				source: String(source).trim(),
				identifier: String(identifier).trim(),
			},
		}),
	);
}

/**
 * @param {unknown} value
 * @returns {string|null}
 */
function nullablePackSignature(value) {
	if (value == null) {
		return null;
	}

	const normalized = String(value).trim();

	return normalized === '' ? null : normalized;
}

/**
 * Prefer pack-level signature, then first image block Source.signature.
 *
 * @param {{signature?: unknown, blocks?: Array<{type?: string, data?: object}>}|null|undefined} pack
 * @returns {string|null}
 */
export function resolvePackSignature(pack) {
	const fromPack = nullablePackSignature(pack?.signature);
	if (fromPack) {
		return fromPack;
	}

	if (!Array.isArray(pack?.blocks)) {
		return null;
	}

	for (const block of pack.blocks) {
		if (block?.type !== 'image') {
			continue;
		}

		const fromBlock = nullablePackSignature(block?.data?.source?.signature);
		if (fromBlock) {
			return fromBlock;
		}
	}

	return null;
}

/**
 * @param {string|null|undefined} raw
 * @returns {{source: string|null, identifier: string|null, agency_id: string|null, signature: string|null, blocks: Array<{type: string, data: object}>}|null}
 */
function parseAgencyMediaPayload(raw) {
	if (!raw) {
		return null;
	}

	let json = raw;
	if (json.startsWith(AGENCY_MEDIA_TEXT_PREFIX)) {
		json = json.slice(AGENCY_MEDIA_TEXT_PREFIX.length);
	}

	try {
		const parsed = JSON.parse(json);
		if (!parsed || !Array.isArray(parsed.blocks)) {
			return null;
		}

		const identifier =
			parsed.identifier != null
				? String(parsed.identifier)
				: parsed.agency_id != null
					? String(parsed.agency_id)
					: null;

		const blocks = parsed.blocks.filter((block) => block && (block.type === 'image' || block.type === 'gallery'));

		return {
			source: parsed.source != null && parsed.source !== '' ? String(parsed.source) : null,
			identifier,
			agency_id: identifier,
			signature: resolvePackSignature({ signature: parsed.signature, blocks }),
			keywords: normalizeSourceKeywords(parsed.keywords),
			tag_ids: normalizeAttributionTags(parsed.tag_ids),
			blocks,
		};
	} catch {
		return null;
	}
}

/**
 * @param {DataTransfer|null|undefined} dataTransfer
 * @returns {{source: string|null, identifier: string|null, agency_id: string|null, signature: string|null, blocks: Array<{type: string, data: object}>}|null}
 */
export function parseAgencyMediaTransfer(dataTransfer) {
	if (!dataTransfer || typeof dataTransfer.getData !== 'function') {
		return null;
	}

	return (
		parseAgencyMediaPayload(dataTransfer.getData(AGENCY_MEDIA_MIME)) ||
		parseAgencyMediaPayload(dataTransfer.getData('text/plain'))
	);
}

/**
 * Resolve a source-media pack from DataTransfer, window pending state, or sessionStorage.
 * Cross-tab / cross-iframe drops often lose MIME types; pending + sessionStorage cover those cases.
 *
 * @param {DataTransfer|null|undefined} dataTransfer
 * @returns {{source: string|null, identifier: string|null, agency_id: string|null, signature: string|null, blocks: Array<{type: string, data: object}>}|null}
 */
export function resolveAgencyMediaDropPack(dataTransfer) {
	const from_transfer = parseAgencyMediaTransfer(dataTransfer);
	if (from_transfer?.blocks?.length) {
		return from_transfer;
	}

	if (typeof window !== 'undefined' && window.__pendingAgencyMediaPack?.blocks?.length) {
		const pending = window.__pendingAgencyMediaPack;
		return {
			source: pending.source ?? null,
			identifier: pending.identifier ?? pending.agency_id ?? null,
			agency_id: pending.identifier ?? pending.agency_id ?? null,
			signature: resolvePackSignature(pending),
			keywords: normalizeSourceKeywords(pending.keywords),
			tag_ids: normalizeAttributionTags(pending.tag_ids),
			blocks: pending.blocks,
		};
	}

	const persisted = readPersistedSourceMediaPack();
	if (!persisted?.blocks?.length) {
		return null;
	}

	const types = Array.from(dataTransfer?.types || []);
	if (types.includes(AGENCY_MEDIA_MIME)) {
		return persisted;
	}

	let plain = '';
	let uri_list = '';
	try {
		plain = dataTransfer?.getData?.('text/plain') || '';
		uri_list = dataTransfer?.getData?.('text/uri-list') || '';
	} catch {
		// Ignore.
	}

	if (plain.startsWith(AGENCY_MEDIA_TEXT_PREFIX)) {
		return persisted;
	}

	const haystack = `${plain}\n${uri_list}`;
	const urls = flattenAgencyMediaUrls(persisted.blocks).map((url) => cleanAgencyMediaUrl(url));
	if (urls.some((url) => url && haystack.includes(url))) {
		return persisted;
	}

	const has_files = types.includes('Files') && (dataTransfer?.files?.length ?? 0) > 0;
	const looks_like_url_drag =
		types.length === 0 ||
		types.some((type) => type === 'text/uri-list' || type === 'text/plain' || type === 'text/html');
	const is_fresh = typeof persisted.ts === 'number' && Date.now() - persisted.ts < 15_000;
	if (!has_files && looks_like_url_drag && (isSourceMediaDragging() || window.__pendingSourceUsage || is_fresh)) {
		return persisted;
	}

	return null;
}

/**
 * Keep pack/usage visible on this window for upload callbacks that run after drop MIME is gone.
 *
 * @param {{source?: string|null, identifier?: string|number|null, agency_id?: string|number|null, signature?: string|null, blocks?: Array<{type: string, data: object}>}|null|undefined} pack
 */
export function stampPendingAgencyMediaPack(pack) {
	if (typeof window === 'undefined' || !pack?.blocks?.length) {
		return;
	}

	const identifier =
		pack.identifier != null && pack.identifier !== ''
			? String(pack.identifier)
			: pack.agency_id != null && pack.agency_id !== ''
				? String(pack.agency_id)
				: null;
	const source = pack.source != null && pack.source !== '' ? String(pack.source) : null;
	const signature = resolvePackSignature(pack);
	const keywords = normalizeSourceKeywords(pack.keywords);
	const tag_ids = normalizeAttributionTags(pack.tag_ids);

	window.__pendingAgencyMediaPack = {
		source,
		identifier,
		agency_id: identifier,
		signature,
		keywords,
		tag_ids,
		blocks: pack.blocks,
	};

	if (source && identifier) {
		window.__pendingSourceUsage = {
			source,
			identifier,
		};
	}
}

/**
 * @param {{source?: string|null, identifier?: string|number|null, agency_id?: string|number|null, signature?: string|null, blocks: Array<{type: string, data: object}>}} payload
 * @param {DataTransfer} dataTransfer
 */
export function setAgencyMediaTransfer(payload, dataTransfer) {
	if (!dataTransfer || typeof dataTransfer.setData !== 'function') {
		return;
	}

	const identifier = payload.identifier ?? payload.agency_id ?? null;
	const blocks = Array.isArray(payload.blocks) ? payload.blocks : [];
	const signature = resolvePackSignature({ signature: payload.signature, blocks });
	const keywords = normalizeSourceKeywords(payload.keywords);
	const tag_ids = normalizeAttributionTags(payload.tag_ids);
	const body = JSON.stringify({
		source: payload.source ?? null,
		identifier,
		agency_id: identifier,
		signature,
		keywords,
		tag_ids,
		blocks,
	});

	try {
		dataTransfer.setData(AGENCY_MEDIA_MIME, body);
	} catch {
		// Some browsers reject custom MIME types.
	}

	try {
		dataTransfer.setData('text/plain', `${AGENCY_MEDIA_TEXT_PREFIX}${body}`);
	} catch {
		// Ignore.
	}

	// Keep a plain image URL available for legacy dropzones that do not parse the pack.
	const firstUrl = flattenAgencyMediaUrls(blocks)[0];
	if (firstUrl) {
		try {
			dataTransfer.setData('text/uri-list', firstUrl);
		} catch {
			// Ignore.
		}
	}
}

/**
 * @param {{source: string, identifier: string, blocks: Array<{type: string, data: object}>}} pack
 */
function broadcastSourceMediaPack(pack) {
	const pending = { source: pack.source, identifier: pack.identifier };
	const message = {
		action: 'naxos-source-media-drag',
		source: pack.source,
		identifier: pack.identifier,
		signature: pack.signature ?? null,
		blocks: pack.blocks,
	};

	const targets = new Set([window]);

	try {
		if (window.parent !== window) {
			targets.add(window.parent);
		}
		if (window.top !== window) {
			targets.add(window.top);
		}
	} catch {
		// Cross-origin parent access is not available.
	}

	const collectFrames = (win) => {
		try {
			win.document.querySelectorAll('iframe').forEach((iframe) => {
				try {
					if (iframe.contentWindow) {
						targets.add(iframe.contentWindow);
					}
				} catch {
					// Cross-origin iframe.
				}
			});
		} catch {
			// Cannot access document.
		}
	};

	collectFrames(window);
	try {
		if (window.top && window.top !== window) {
			collectFrames(window.top);
		}
	} catch {
		// Cross-origin top.
	}

	for (const target of targets) {
		try {
			target.__pendingSourceUsage = pending;
			target.__pendingAgencyMediaPack = pack;
			if (target !== window) {
				target.postMessage(message, window.location.origin);
			}
		} catch {
			// Ignore cross-origin write failures.
		}
	}

	notifyDetachedSourcesPicker(pack);
}

/**
 * When running inside a detached sources-picker tab, publish the pack to the edit host.
 *
 * @param {{source: string, identifier: string, blocks: Array<{type: string, data: object}>}} pack
 */
function notifyDetachedSourcesPicker(pack) {
	const bus = typeof window !== 'undefined' ? window.__naxosSourcesPickerBus : null;
	const sessionId = typeof window !== 'undefined' ? window.__naxosSourcesPickerSessionId : null;

	if (!bus || !sessionId) {
		return;
	}

	bus.send('DRAG_PACK', pack);
	writeSourcesPickerPack(sessionId, pack);
}

/**
 * Start a source-media drag: MIME pack + pending usage on this window, parents and iframes.
 *
 * @param {{source?: string|null, identifier?: string|number|null, signature?: string|null, keywords?: string[]|null, tag_ids?: number[], url?: string|null, caption?: string, blocks?: Array<{type: string, data: object}>}} options
 * @param {DataTransfer|null|undefined} dataTransfer
 */
export function beginSourceMediaDrag(options, dataTransfer) {
	const source = options?.source ? String(options.source) : null;
	const identifier =
		options?.identifier != null && options.identifier !== '' ? String(options.identifier) : null;
	const signature =
		options?.signature != null && String(options.signature).trim() !== ''
			? String(options.signature).trim()
			: null;
	const keywords = normalizeSourceKeywords(options?.keywords);
	const tag_ids = normalizeAttributionTags(options?.tag_ids);

	let blocks = Array.isArray(options?.blocks) ? options.blocks : [];

	if (blocks.length === 0 && options?.url) {
		blocks = [
			{
				type: 'image',
				data: {
					file: { url: cleanAgencyMediaUrl(String(options.url)) },
					caption: options.caption || '',
					source: {
						name: source,
						identifier,
						signature,
						url: cleanAgencyMediaUrl(String(options.url)),
						file: null,
						keywords,
					},
					attribution: {
						journalist_id: null,
						tags: tag_ids,
					},
				},
			},
		];
	}

	if (blocks.length === 0) {
		return false;
	}

	const pack = {
		source,
		identifier,
		signature,
		keywords,
		tag_ids,
		agency_id: identifier,
		blocks,
	};

	if (dataTransfer) {
		setAgencyMediaTransfer(pack, dataTransfer);
	}

	if (source && identifier) {
		// Persist immediately on dragstart so save works even if drop MIME/events are lost
		// (typical when dragging from Collaboratori page into a story panel iframe).
		setSourceMediaDragging(true);
		persistSourceMediaPack(pack);
		queueSourceUsage(source, identifier);
		broadcastSourceMediaPack(pack);
	}

	return true;
}
